import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Rocket,
  Loader2,
  Search,
  Download,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminDemoLeadsPage — single page hosting:
 *  1. Master toggle that controls the "Explore Live Demo" CTA on the
 *     public Login Dashboard (writes to site_config.demo_button_enabled).
 *  2. Paginated table of captured DemoLeads with CSV + XLSX export.
 */
export function AdminDemoLeadsPage() {
  const { admin, loading } = useAdminAuth();
  const [demoEnabled, setDemoEnabled] = useState(true);
  const [savingToggle, setSavingToggle] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [exporting, setExporting] = useState(null); // 'csv' | 'xlsx' | null
  const pageSize = 25;

  // Load the master toggle from site config.
  useEffect(() => {
    if (!admin) return;
    adminApi
      .get('/admin/site-config')
      .then((r) => setDemoEnabled(r.data?.demo_button_enabled !== false))
      .catch(() => {});
  }, [admin]);

  const fetchLeads = useCallback(() => {
    if (!admin) return;
    setLoadingLeads(true);
    adminApi
      .get('/admin/demo-leads', { params: { page, page_size: pageSize, search: search || undefined } })
      .then((r) => {
        setLeads(r.data?.items || []);
        setTotal(r.data?.total || 0);
      })
      .catch(() => toast.error('Could not load demo leads'))
      .finally(() => setLoadingLeads(false));
  }, [admin, page, search]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const toggleDemo = async () => {
    const next = !demoEnabled;
    setSavingToggle(true);
    setDemoEnabled(next);
    try {
      await adminApi.put('/admin/site-config', { demo_button_enabled: next });
      toast.success(`Demo button ${next ? 'enabled' : 'disabled'} on the Login page`);
      try { window.dispatchEvent(new Event('site-config-updated')); } catch {}
    } catch (err) {
      setDemoEnabled(!next);
      toast.error('Could not update toggle. Try again.');
    } finally {
      setSavingToggle(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${BACKEND_URL}/api/admin/demo-leads/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `demo_leads.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded demo_leads.${format}`);
    } catch (err) {
      toast.error(`Could not export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div
      data-testid="admin-demo-leads-page"
      className="min-h-screen"
      style={{
        background:
          'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)',
      }}
    >
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              data-testid="admin-demo-leads-back"
              className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800"
            >
              <ArrowLeft className="w-4 h-4 text-slate-300" />
            </Link>
            <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <Rocket className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <div className="text-white font-semibold">Demo &amp; Leads</div>
              <div className="text-xs text-slate-400">
                Control the public sandbox button and review captured leads.
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {/* Master toggle */}
        <section
          data-testid="demo-button-toggle-section"
          className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 flex items-center justify-between gap-4"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-white font-semibold">Explore Live Demo button</h2>
              <p className="text-sm text-slate-400 mt-0.5 max-w-xl">
                When enabled, a gold CTA appears on the Login Dashboard letting prospects spin up a
                pre-authenticated 24-hour sandbox. Disable to hide the button globally.
              </p>
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={demoEnabled}
            onClick={toggleDemo}
            disabled={savingToggle}
            data-testid="demo-button-toggle"
            className={`relative inline-flex h-7 w-12 rounded-full transition-colors flex-shrink-0 ${
              demoEnabled ? 'bg-amber-500' : 'bg-slate-700'
            } ${savingToggle ? 'opacity-60' : ''}`}
          >
            <span
              className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform mt-1 ${
                demoEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </section>

        {/* Leads */}
        <section className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-white font-semibold">Captured Leads</h2>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  data-testid="demo-leads-search"
                  type="search"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="Search name, email or phone"
                  className="h-9 pl-8 bg-slate-950 border-slate-700 text-slate-100 placeholder:text-slate-500 w-64"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => handleExport('csv')}
                disabled={!!exporting || total === 0}
                data-testid="demo-leads-export-csv"
                className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white"
              >
                {exporting === 'csv' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExport('xlsx')}
                disabled={!!exporting || total === 0}
                data-testid="demo-leads-export-xlsx"
                className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white"
              >
                {exporting === 'xlsx' ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
                XLSX
              </Button>
            </div>
          </div>

          {loadingLeads ? (
            <div className="text-slate-400 flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : leads.length === 0 ? (
            <div
              data-testid="demo-leads-empty"
              className="text-center text-slate-500 py-12 border border-dashed border-slate-800 rounded-xl"
            >
              No leads captured yet. They'll appear here once a sandbox visitor unlocks a high-value action.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-800">
              <table className="w-full text-sm" data-testid="demo-leads-table">
                <thead className="bg-slate-950/80 text-slate-400 uppercase text-[11px] tracking-wider">
                  <tr>
                    <th className="px-3 py-2.5 text-left">Name</th>
                    <th className="px-3 py-2.5 text-left">Email</th>
                    <th className="px-3 py-2.5 text-left">Phone</th>
                    <th className="px-3 py-2.5 text-left">Tier</th>
                    <th className="px-3 py-2.5 text-left">Sandbox Org</th>
                    <th className="px-3 py-2.5 text-left">Captured At</th>
                  </tr>
                </thead>
                <tbody className="text-slate-200">
                  {leads.map((l) => (
                    <tr
                      key={l.id}
                      data-testid={`demo-lead-row-${l.id}`}
                      className="border-t border-slate-800 hover:bg-slate-800/40"
                    >
                      <td className="px-3 py-2.5 font-medium">{l.name}</td>
                      <td className="px-3 py-2.5 text-slate-300">{l.email}</td>
                      <td className="px-3 py-2.5 text-slate-300">{l.phone}</td>
                      <td className="px-3 py-2.5">
                        {l.selected_tier ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">
                            {l.selected_tier}
                          </span>
                        ) : (
                          <span className="text-slate-500">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs text-slate-400">
                        {l.associated_temp_org || '—'}
                      </td>
                      <td className="px-3 py-2.5 text-slate-400">
                        {(l.captured_at || '').replace('T', ' ').slice(0, 16)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>
                Page {page} of {totalPages} · {total} leads
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white h-8 px-3"
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white h-8 px-3"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

export default AdminDemoLeadsPage;
